import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import pb from './services/pocketbase';

// Pages
import SplashScreen from './pages/SplashScreen';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import CreatePostChoose from './pages/CreatePostChoose';
import Profile from './pages/Profile';
import VolunteersCatalog from './pages/VolunteersCatalog';
import VolunteersRequests from './pages/VolunteersRequests';
import Chats from './pages/Chats';
import ChatRoom from './pages/ChatRoom';
import Services from './pages/Services';
import { MoreMenu } from './pages/PlaceholderPages';
import Blacklist from './pages/Blacklist';

// Styles
import './styles/App.css';

// Глобальный обработчик ошибок — перехватывает краши любого компонента
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
    window.location.href = '/home';
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px', background:'#F5F9FF', textAlign:'center'}}>
          <div style={{fontSize:'64px', marginBottom:'16px'}}>🐾</div>
          <div style={{fontWeight:'700', fontSize:'20px', color:'#333', marginBottom:'8px'}}>Что-то пошло не так</div>
          <div style={{fontSize:'14px', color:'#666', marginBottom:'32px', maxWidth:'280px'}}>
            Произошла неожиданная ошибка. Попробуйте вернуться на главную.
          </div>
          <button
            onClick={() => this.handleReset()}
            style={{padding:'14px 32px', background:'#3B5998', color:'white', border:'none', borderRadius:'12px', fontSize:'15px', fontWeight:'700', cursor:'pointer'}}
          >
            На главную
          </button>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={{marginTop:'24px', fontSize:'11px', color:'#999', textAlign:'left', maxWidth:'100%', overflow:'auto', background:'#fff', padding:'12px', borderRadius:'8px'}}>
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// Компонент-защита для страниц требующих авторизации
function RequireAuth({ user, children }) {
  if (!user) {
    return (
      <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end'}}>
        <div style={{background:'white', borderRadius:'24px 24px 0 0', width:'100%', padding:'24px'}}>
          <div style={{textAlign:'center', marginBottom:'20px'}}>
            <div style={{fontSize:'40px', marginBottom:'8px'}}>🐾</div>
            <div style={{fontWeight:'700', fontSize:'18px', marginBottom:'6px'}}>Нужна авторизация</div>
            <div style={{fontSize:'14px', color:'#666'}}>Войдите или зарегистрируйтесь чтобы продолжить</div>
          </div>
          <Link to="/login" style={{display:'block', padding:'14px', background:'#3B5998', color:'white', borderRadius:'12px', textAlign:'center', fontSize:'15px', fontWeight:'700', textDecoration:'none', marginBottom:'10px'}}>
            Войти
          </Link>
          <Link to="/register" style={{display:'block', padding:'14px', background:'#F5F9FF', color:'#3B5998', borderRadius:'12px', textAlign:'center', fontSize:'15px', fontWeight:'700', textDecoration:'none', border:'2px solid #E3F2FD'}}>
            Зарегистрироваться
          </Link>
        </div>
      </div>
    );
  }
  return children;
}

// Сохраняем OneSignal Player ID в профиль пользователя
// OneSignal уже инициализирован в index.js — здесь только читаем ID
const saveOneSignalId = async (userId) => {
  try {
    const { default: OneSignal } = await import('react-onesignal');
    const subscriptionId = OneSignal.User.PushSubscription.id;
    if (subscriptionId && userId) {
      await pb.collection('users').update(userId, { oneSignalId: subscriptionId });
    }
  } catch (e) {
    console.log('OneSignal ID save error:', e);
  }
};

const CreatePost = React.lazy(() => import('./pages/CreatePost'));
const PostDetail = React.lazy(() => import('./pages/PostDetail'));
const ServiceCategory = React.lazy(() => import('./pages/ServiceCategory'));

function RouteLoader() {
  return (
    <div className="loading-state">
      <div className="spinner"></div>
    </div>
  );
}

function App() {
  const [user, setUser] = React.useState(pb.authStore.model);
  const [showSplash, setShowSplash] = React.useState(true);
  const [isAppReady, setIsAppReady] = React.useState(false);

  React.useEffect(() => {
    // OneSignal уже инициализирован в index.js
    // Сохраняем Player ID если пользователь уже авторизован
    if (pb.authStore.model?.id) {
      setTimeout(() => saveOneSignalId(pb.authStore.model.id), 2000);
    }

    const unsubscribe = pb.authStore.onChange((token, model) => {
      setUser(model);
      // Сохраняем Player ID при входе
      if (model?.id) {
        setTimeout(() => saveOneSignalId(model.id), 2000);
      }
    });

    setIsAppReady(true);

    return () => {
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (!isAppReady) return undefined;

    const splashTimer = setTimeout(() => {
      setShowSplash(false);
    }, 500);

    return () => clearTimeout(splashTimer);
  }, [isAppReady]);

  if (showSplash) return <SplashScreen />;

  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route path="/login" element={!user ? <Login /> : <Navigate to="/home" />} />
              <Route path="/register" element={!user ? <Register /> : <Navigate to="/home" />} />

              {/* Публичные страницы — доступны без авторизации */}
              <Route path="/home" element={<Home />} />
              <Route path="/post/:id" element={<PostDetail />} />
              <Route path="/volunteers" element={<VolunteersCatalog />} />
              <Route path="/volunteers/requests" element={<VolunteersRequests />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:type" element={<ServiceCategory />} />

              {/* Страницы требующие авторизации */}
              <Route path="/create-post/choose" element={<RequireAuth user={user}><CreatePostChoose /></RequireAuth>} />
              <Route path="/create-post" element={<RequireAuth user={user}><CreatePost /></RequireAuth>} />
              <Route path="/profile" element={<RequireAuth user={user}><Profile /></RequireAuth>} />
              <Route path="/profile/:id" element={<Profile />} />
              <Route path="/chats" element={<RequireAuth user={user}><Chats /></RequireAuth>} />
              <Route path="/chat/:chatId" element={<RequireAuth user={user}><ChatRoom /></RequireAuth>} />
              <Route path="/more" element={<RequireAuth user={user}><MoreMenu /></RequireAuth>} />
              <Route path="/blacklist" element={<Blacklist />} />

              <Route path="/" element={<Navigate to="/home" />} />
              <Route path="*" element={<Navigate to="/home" />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
